import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button, Form, Container, Row, Col, Card, Spinner } from "react-bootstrap";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import AsyncSelect from "react-select/async";
import toast from 'react-hot-toast';
import { useFormik } from "formik";
import { getCategoryListingPaginate, getManufacturesListingPaginate, getSubCategoryListingPaginate } from "../../api/context/manufacturer";
import { IoMdArrowBack } from "react-icons/io";
import * as yup from 'yup';
import { useNavigate, useParams } from "react-router-dom";
import { FiImage } from "react-icons/fi";
import { getBrandListing } from "../../api/context/brand";
import { createUpdateOfferPromocodes, getOfferPromocodeById } from "../../api/context/promocodes";
import Loader from "../../components/loader/loader";



// Validation schema
const validationSchema = yup.object().shape({
    name: yup.string().required('Name is required'),
    code: yup.string().required('Code is required'),
    description: yup.string().required('Description is required'),
    expiryDate: yup.string().required('Expiry date is required'),
    minimumCartAmount: yup.number()
        .typeError('Minimum cart amount must be a number')
        .positive('Minimum cart amount must be positive')
        .required('Minimum cart amount is required'),
    discountPercentage: yup.number()
        .typeError('Discount percentage must be a number')
        .min(1, 'Discount percentage must be at least 1%')
        .max(100, 'Discount percentage cannot exceed 100%')
        .required('Discount percentage is required'),
    redeemCount: yup.number()
        .typeError('Redeem count must be a number')
        .integer('Redeem count must be a whole number')
        .min(1, 'Redeem count must be at least 1')
        .required('Redeem count is required'),


});


// Promo Code Summary Card Component
const PromoCodeSummary = ({ formik }) => {
    const {
        vendorSelection,
        brandSelection,
        categorySelection,
        subcategorySelection,
        discountPercentage,
        minimumCartAmount
    } = formik.values;

    console.log('📋 Summary - Formik values:', formik.values);

    // Helper function to get selection count
    const getSelectionCount = (selection, type) => {
        const fieldName = `selected${type.charAt(0).toUpperCase() + type.slice(1)}`;
        return selection[fieldName]?.length || 0;
    };

    const getSelectionSummary = (selection, type) => {
        const count = getSelectionCount(selection, type);

        switch (selection.mode) {
            case 'includeAll':
                return `All ${type}`;
            case 'includeSpecific':
                return `${count} ${type.toLowerCase()} included`;
            case 'excludeAll':
                return `All ${type} excluded`;
            case 'excludeSpecific':
                return `${count} ${type.toLowerCase()} excluded`;
            default:
                return `All ${type}`;
        }
    };

    const getSpecificItems = (selection, type) => {
        const fieldName = `selected${type.charAt(0).toUpperCase() + type.slice(1)}`;
        const items = selection[fieldName] || [];
        if (items.length === 0) return null;

        return items.map(item => item.label).join(', ');
    };

    const isPromoValid = () => {
        // Check if at least one selection includes some items
        const hasVendors = vendorSelection.mode !== 'excludeAll';
        const hasBrands = brandSelection.mode !== 'excludeAll';
        const hasCategories = categorySelection.mode !== 'excludeAll';
        const hasSubcategories = subcategorySelection.mode !== 'excludeAll';

        return hasVendors || hasBrands || hasCategories || hasSubcategories;
    };

    const getApplicabilityMessage = () => {
        if (!isPromoValid()) {
            return "This promo code won't apply to any products. Please adjust your selections.";
        }

        const includedParts = [];
        const excludedParts = [];

        // Helper function to get selection details
        const getSelectionDetails = (selection, type) => {
            const isPlural = type.endsWith('s') ? type.slice(0, -1) + 'ies' : type + 's';
            const isSingular = type.endsWith('s') ? type.slice(0, -1) + 'y' : type;

            if (selection.mode === 'includeAll') {
                return { type: 'include', text: `all ${type}` };
            } else if (selection.mode === 'includeSpecific') {
                const count = getSelectionCount(selection, type);
                if (count > 0) {
                    return {
                        type: 'include',
                        text: `${count} specific ${count > 1 ? isPlural : isSingular}`
                    };
                }
            } else if (selection.mode === 'excludeAll') {
                return { type: 'exclude', text: `all ${type}` };
            } else if (selection.mode === 'excludeSpecific') {
                const count = getSelectionCount(selection, type);
                if (count > 0) {
                    return {
                        type: 'exclude',
                        text: `${count} specific ${count > 1 ? isPlural : isSingular}`
                    };
                }
            }
            return null;
        };

        // Get selection details for all types
        const vendorDetails = getSelectionDetails(vendorSelection, 'vendor');
        const brandDetails = getSelectionDetails(brandSelection, 'brand');
        const categoryDetails = getSelectionDetails(categorySelection, 'category');
        const subcategoryDetails = getSelectionDetails(subcategorySelection, 'subcategory');

        // Build the logic description
        let message = "This promo will apply to ";

        // Check if we have any specific inclusions
        const hasSpecificInclusions = [vendorDetails, brandDetails, categoryDetails, subcategoryDetails]
            .some(detail => detail?.type === 'include' && detail.text.includes('specific'));

        if (hasSpecificInclusions) {
            // Build combination-based message
            const specificParts = [];

            if (vendorDetails?.type === 'include') {
                specificParts.push(vendorDetails.text);
            }
            if (brandDetails?.type === 'include') {
                specificParts.push(brandDetails.text);
            }
            if (categoryDetails?.type === 'include') {
                specificParts.push(categoryDetails.text);
            }
            if (subcategoryDetails?.type === 'include') {
                specificParts.push(subcategoryDetails.text);
            }

            if (specificParts.length > 0) {
                if (specificParts.length === 1) {
                    message += `${specificParts[0]}`;
                } else if (specificParts.length === 2) {
                    message += `${specificParts[0]} and ${specificParts[1]}`;
                } else {
                    message += specificParts.slice(0, -1).join(', ') + ', and ' + specificParts[specificParts.length - 1];
                }

                // Add context about relationships
                if (vendorDetails?.type === 'include' && (brandDetails?.type === 'include' || categoryDetails?.type === 'include')) {
                    message += " - meaning products from selected vendors that also match the selected ";
                    const additionalCriteria = [];
                    if (brandDetails?.type === 'include') additionalCriteria.push('brands');
                    if (categoryDetails?.type === 'include') additionalCriteria.push('categories');
                    if (subcategoryDetails?.type === 'include') additionalCriteria.push('subcategories');

                    if (additionalCriteria.length === 1) {
                        message += additionalCriteria[0];
                    } else if (additionalCriteria.length === 2) {
                        message += `${additionalCriteria[0]} and ${additionalCriteria[1]}`;
                    } else {
                        message += additionalCriteria.slice(0, -1).join(', ') + ', and ' + additionalCriteria[additionalCriteria.length - 1];
                    }
                }
            }
        } else {
            // Handle "all" cases and exclusions
            const allIncludedParts = [vendorDetails, brandDetails, categoryDetails, subcategoryDetails]
                .filter(detail => detail?.type === 'include')
                .map(detail => detail.text);

            if (allIncludedParts.length > 0) {
                if (allIncludedParts.length === 1) {
                    message += allIncludedParts[0];
                } else if (allIncludedParts.length === 2) {
                    message += `${allIncludedParts[0]} and ${allIncludedParts[1]}`;
                } else {
                    message += allIncludedParts.slice(0, -1).join(', ') + ', and ' + allIncludedParts[allIncludedParts.length - 1];
                }
            } else {
                message += "all products";
            }
        }

        // Add exclusions
        const exclusionDetails = [vendorDetails, brandDetails, categoryDetails, subcategoryDetails]
            .filter(detail => detail?.type === 'exclude')
            .map(detail => detail.text);

        if (exclusionDetails.length > 0) {
            message += " except ";
            if (exclusionDetails.length === 1) {
                message += exclusionDetails[0];
            } else if (exclusionDetails.length === 2) {
                message += `${exclusionDetails[0]} and ${exclusionDetails[1]}`;
            } else {
                message += exclusionDetails.slice(0, -1).join(', ') + ', and ' + exclusionDetails[exclusionDetails.length - 1];
            }
        }

        message += ` with minimum cart amount of ₹${minimumCartAmount || 0}.`;

        return message;
    };

    return (
        <Card className="mb-4 shadow-sm border-success" style={{ borderWidth: '2px' }}>
            <Card.Header className="bg-success text-white py-3">
                <div className="d-flex align-items-center">
                    <div className="bg-white text-success rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{ width: '32px', height: '32px', fontSize: '16px', fontWeight: 'bold' }}>
                        ✓
                    </div>
                    <div>
                        <h5 className="mb-0 fw-bold">Promo Code Summary</h5>
                        <small className="opacity-90">Overview of what this promo code will apply to</small>
                    </div>
                </div>
            </Card.Header>

            <Card.Body className="p-4">
                {/* Discount Info */}
                <div className="row mb-4">
                    <div className="col-md-4">
                        <div className="d-flex align-items-center mb-3">
                            <div className="bg-light rounded p-2 me-3">
                                <span className="fw-bold text-success" style={{ fontSize: '18px' }}>
                                    {discountPercentage || 0}%
                                </span>
                            </div>
                            <div>
                                <small className="text-muted d-block">Discount</small>
                                <span className="fw-semibold">{discountPercentage || 0}% OFF</span>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-4">
                        <div className="d-flex align-items-center mb-3">
                            <div className="bg-light rounded p-2 me-3">
                                <span className="fw-bold text-success" style={{ fontSize: '18px' }}>
                                    {formik.values.redeemCount || 0}
                                </span>
                            </div>
                            <div>
                                <small className="text-muted d-block">Max Redemptions</small>
                                <span className="fw-semibold">{formik.values.redeemCount || 0} times</span>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="d-flex align-items-center mb-3">
                            <div className="bg-light rounded p-2 me-3">
                                <span className="fw-bold text-success" style={{ fontSize: '18px' }}>
                                    ₹{minimumCartAmount || 0}
                                </span>
                            </div>
                            <div>
                                <small className="text-muted d-block">Minimum Cart Value</small>
                                <span className="fw-semibold">₹{minimumCartAmount || 0}+</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Selection Breakdown */}
                <div className="row">
                    <div className="col-md-6">
                        <h6 className="fw-semibold mb-3 text-muted">APPLICATION SCOPE</h6>

                        {/* Vendors */}
                        {vendorSelection && (
                            <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                                <span className="fw-medium">Vendors:</span>
                                <span className={`badge ${vendorSelection.mode.includes('include') ? 'bg-success' : 'bg-danger'}`}>
                                    {getSelectionSummary(vendorSelection, 'Vendor')}
                                </span>
                            </div>
                        )}

                        {/* Brands */}
                        {brandSelection && (
                            <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                                <span className="fw-medium">Brands:</span>
                                <span className={`badge ${brandSelection.mode.includes('include') ? 'bg-success' : 'bg-danger'}`}>
                                    {getSelectionSummary(brandSelection, 'Brand')}
                                </span>
                            </div>
                        )}

                        {/* Categories */}
                        {categorySelection && (
                            <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                                <span className="fw-medium">Categories:</span>
                                <span className={`badge ${categorySelection.mode.includes('include') ? 'bg-success' : 'bg-danger'}`}>
                                    {getSelectionSummary(categorySelection, 'Category')}
                                </span>
                            </div>
                        )}

                        {/* Subcategories */}
                        {subcategorySelection && (
                            <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                                <span className="fw-medium">Subcategories:</span>
                                <span className={`badge ${subcategorySelection.mode.includes('include') ? 'bg-success' : 'bg-danger'}`}>
                                    {getSelectionSummary(subcategorySelection, 'Subcategory')}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="col-md-6">
                        <h6 className="fw-semibold mb-3 text-muted">APPLICABILITY MESSAGE</h6>
                        <div className={`alert ${isPromoValid() ? 'alert-success' : 'alert-warning'} mb-0`}>
                            <div className="d-flex">
                                <div className="me-3 flex-shrink-0">
                                    {isPromoValid() ? (
                                        <span className="text-success">✓</span>
                                    ) : (
                                        <span className="text-warning">⚠</span>
                                    )}
                                </div>
                                <div>
                                    <small className="fw-medium d-block mb-1">
                                        {isPromoValid() ? 'Promo Code Will Apply To:' : 'Attention Required:'}
                                    </small>
                                    <span style={{ fontSize: '13px', lineHeight: '1.4' }}>
                                        {getApplicabilityMessage()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
};

// Reusable Parameter Selection Card Component
const ParameterSelectionCard = ({
    title,
    selectionType,
    loadOptions,
    defaultOptions,
    formik,
    disabled = false,
    isSubCategory = false,
    hideExcludeAll = false
}) => {
    const selection = formik.values[`${selectionType}Selection`];

    // Helper function to get the correct field name
    const getSelectionFieldName = () => {
        const typeCapitalized = selectionType.charAt(0).toUpperCase() + selectionType.slice(1);
        return `selected${typeCapitalized}`;
    };

    const customNoOptionsMessage = ({ inputValue }) => {
        return "No options found";
    };

    const handleSelectionChange = (selectedOptions) => {
        const fieldName = `${selectionType}Selection.${getSelectionFieldName()}`;
        console.log(`🔄 Updating ${fieldName}:`, selectedOptions);
        formik.setFieldValue(fieldName, selectedOptions || []);
    };

    const getCurrentSelection = () => {
        return selection[getSelectionFieldName()] || [];
    };

    useEffect(() => {
        console.log(`📊 ${selectionType} Selection updated:`, {
            mode: selection.mode,
            selectedItems: getCurrentSelection()
        });
    }, [selection.mode, getCurrentSelection()]);

    const handleModeChange = (newMode) => {
        if (!disabled) {
            const currentMode = selection.mode;
            const currentSelections = getCurrentSelection();

            console.log(`🔄 Changing ${selectionType} mode from ${currentMode} to ${newMode}`);

            // Clear selections when switching from specific to non-specific modes
            if ((currentMode === 'includeSpecific' || currentMode === 'excludeSpecific') &&
                (newMode === 'includeAll' || newMode === 'excludeAll')) {

                formik.setFieldValue(`${selectionType}Selection.mode`, newMode);
                formik.setFieldValue(`${selectionType}Selection.${getSelectionFieldName()}`, []);
            } else {
                // Normal mode change
                formik.setFieldValue(`${selectionType}Selection.mode`, newMode);
            }
        }
    };

    const getSelectionText = () => {
        const currentSelections = getCurrentSelection();

        switch (selection.mode) {
            case 'includeAll':
                return 'All items included';
            case 'includeSpecific':
                const includeCount = currentSelections.length;
                return `${includeCount} item${includeCount !== 1 ? 's' : ''} included`;
            case 'excludeAll':
                return 'All items excluded';
            case 'excludeSpecific':
                const excludeCount = currentSelections.length;
                return `${excludeCount} item${excludeCount !== 1 ? 's' : ''} excluded`;
            default:
                return 'Select option';
        }
    };

    const getModeColor = (mode) => {
        switch (mode) {
            case 'includeAll':
            case 'includeSpecific':
                return 'success';
            case 'excludeAll':
            case 'excludeSpecific':
                return 'danger';
            default:
                return 'secondary';
        }
    };

    // Available modes based on hideExcludeAll prop
    const availableModes = hideExcludeAll
        ? ['includeAll', 'includeSpecific', 'excludeSpecific']
        : ['includeAll', 'includeSpecific', 'excludeAll', 'excludeSpecific'];

    const modeConfig = {
        includeAll: {
            icon: '✓',
            title: 'Include All',
            description: `Include all ${title.toLowerCase()} in this promotional offer`,
            color: 'success'
        },
        includeSpecific: {
            icon: '+',
            title: 'Include Specific',
            description: `Choose specific ${title.toLowerCase()} to include in this promotion`,
            color: 'success'
        },
        excludeAll: {
            icon: '✗',
            title: 'Exclude All',
            description: `Exclude all ${title.toLowerCase()} from this promotional offer`,
            color: 'danger'
        },
        excludeSpecific: {
            icon: '-',
            title: 'Exclude Specific',
            description: `Select specific ${title.toLowerCase()} to exclude from this promotion`,
            color: 'danger'
        }
    };

    return (
        <Card className="mb-4 shadow-sm border-1">
            <Card.Header className="bg-light d-flex justify-content-between align-items-center py-3">
                <span style={{ fontSize: '16px', fontWeight: '600' }}>
                    {title}
                </span>
                <div className="d-flex align-items-center">
                    <span
                        className={`badge ${disabled ? 'bg-secondary' : `bg-${getModeColor(selection.mode)}`} me-2`}
                        style={{ fontSize: '12px', fontWeight: '500' }}
                    >
                        {disabled ? 'Disabled' : selection.mode.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </span>
                    <span
                        className="text-muted fw-semibold"
                        style={{ fontSize: '13px' }}
                    >
                        {getSelectionText()}
                    </span>
                </div>
            </Card.Header>

            <Card.Body className="p-0">
                {/* Selection Mode Cards */}
                <div className="row g-0">
                    {availableModes.map((mode) => {
                        const config = modeConfig[mode];
                        return (
                            <div
                                key={mode}
                                className={`col-md-${hideExcludeAll ? '4' : '3'} p-3`}
                            >
                                <div
                                    className={`h-100 border rounded-3 p-3 cursor-pointer transition-all bg-light bg-opacity-25 text-dark ${selection.mode === mode
                                        ? `border-${config.color}`
                                        : 'border-light-subtle'
                                        } ${disabled ? 'opacity-50' : ''}`}
                                    onClick={() => handleModeChange(mode)}
                                    style={{
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s ease-in-out',
                                        minHeight: '120px'
                                    }}
                                >
                                    <div className="d-flex align-items-start mb-2">
                                        <div className={`rounded-circle d-flex align-items-center justify-content-center me-2 ${selection.mode === mode
                                            ? `bg-${config.color} text-white`
                                            : 'bg-white text-muted border'
                                            }`}
                                            style={{
                                                width: '24px',
                                                height: '24px',
                                                fontSize: '14px',
                                                fontWeight: 'bold'
                                            }}>
                                            {selection.mode === mode ? config.icon : ''}
                                        </div>
                                        <h6 className="mb-1 text-dark">
                                            {config.title}
                                        </h6>
                                    </div>
                                    <p className="small mb-0 text-muted">
                                        {config.description}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Dropdown for Include/Exclude Specific Modes */}
                {(selection.mode === 'includeSpecific' || selection.mode === 'excludeSpecific') && (
                    <div className="p-4 border-top">
                        <Form.Group className="mb-0">
                            <Form.Label className="fw-semibold d-flex align-items-center" style={{ fontSize: "14px" }}>
                                <span className={`badge ${selection.mode === 'includeSpecific' ? 'bg-success' : 'bg-danger'} me-2`}>
                                    {selection.mode === 'includeSpecific' ? 'Inclusion' : 'Exclusion'}
                                </span>
                                {selection.mode === 'includeSpecific'
                                    ? `Select ${title.toLowerCase()} to include in this promotion:`
                                    : `Select ${title.toLowerCase()} to exclude from this promotion:`}
                            </Form.Label>
                            <div className="mt-2">
                                <AsyncSelect
                                    loadOptions={loadOptions}
                                    defaultOptions={defaultOptions}
                                    classNamePrefix="select"
                                    isClearable={true}
                                    isMulti
                                    placeholder={`Search and select ${title.toLowerCase()}...`}
                                    onChange={handleSelectionChange}
                                    value={getCurrentSelection()}
                                    noOptionsMessage={customNoOptionsMessage}
                                    isDisabled={disabled}
                                    styles={{
                                        menu: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                                        }),
                                        control: (base, state) => ({
                                            ...base,
                                            borderRadius: "8px",
                                            minHeight: "48px",
                                            fontSize: "14px",
                                            backgroundColor: disabled ? '#f8f9fa' : '#fff',
                                        }),
                                        valueContainer: (base) => ({
                                            ...base,
                                            display: "flex",
                                            flexWrap: "nowrap",
                                            overflowX: "auto",
                                            overflowY: "hidden",
                                            scrollbarWidth: "none",
                                            msOverflowStyle: "none",
                                            "::-webkit-scrollbar": {
                                                display: "none",
                                            },
                                        }),
                                        input: (base) => ({
                                            ...base,
                                            minWidth: "80px",
                                            flexShrink: 0,
                                        }),
                                        multiValue: (base) => ({
                                            ...base,
                                            backgroundColor: selection.mode === 'includeSpecific' ? '#198754' : '#dc3545',
                                            color: 'white',
                                            borderRadius: "16px",
                                            padding: "2px 8px",
                                            flexShrink: 0,
                                            fontWeight: '500'
                                        }),
                                        multiValueLabel: (base) => ({
                                            ...base,
                                            color: 'white',
                                            fontSize: "12px",
                                        }),
                                        multiValueRemove: (base) => ({
                                            ...base,
                                            color: 'white',
                                            ':hover': {
                                                backgroundColor: selection.mode === 'includeSpecific' ? '#218838' : '#c82333',
                                                color: 'white',
                                            },
                                        }),
                                    }}
                                />
                            </div>
                            <div className="mt-2">
                                <small className="text-muted">
                                    {selection.mode === 'includeSpecific'
                                        ? `Selected ${getCurrentSelection().length} ${title.toLowerCase()} will be included in this promotion`
                                        : `Selected ${getCurrentSelection().length} ${title.toLowerCase()} will be excluded from this promotion`}
                                </small>
                            </div>
                        </Form.Group>
                    </div>
                )}

                {/* All Mode Messages */}
                {selection.mode === 'includeAll' && (
                    <div className="p-4 border-top bg-opacity-10">
                        <div className="d-flex align-items-center">
                            <div>
                                <h6 className="text-success mb-1">All {title} Included</h6>
                                <p className="text-muted small mb-0">
                                    All {title.toLowerCase()} in your system will be eligible for this promotional offer.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {selection.mode === 'excludeAll' && (
                    <div className="p-4 border-top bg-opacity-10">
                        <div className="d-flex align-items-center">
                            <div>
                                <h6 className="text-danger mb-1">All {title} Excluded</h6>
                                <p className="text-muted small mb-0">
                                    All {title.toLowerCase()} in your system will be excluded from this promotional offer.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

const CreatePromoCodesPage = () => {
    const navigate = useNavigate();
    const [manufacture, setManufacture] = useState([]);
    const [category, setCategory] = useState([]);
    const [subcategory, setSubCategory] = useState([]);
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(false);
    const [buttonLoading, setButtonLoading] = useState(false);
    const { id } = useParams();
    const fileInputRef = useRef(null);


    const user = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || '{}') : {};

    // Formik initialization with enhanced selection state
    const formik = useFormik({
        initialValues: {
            name: "",
            code: "",
            description: "",
            minimumCartAmount: "",
            discountPercentage: "",
            redeemCount: "",
            image: "",
            expiryDate: "",
            img_view: "",

            // Corrected field names
            vendorSelection: {
                mode: 'includeAll',
                selectedVendor: [] // Changed from selectedVendors
            },
            brandSelection: {
                mode: 'includeAll',
                selectedBrand: [] // Changed from selectedBrands
            },
            categorySelection: {
                mode: 'includeAll',
                selectedCategory: [] // Changed from selectedCategories
            },
            subcategorySelection: {
                mode: 'includeAll',
                selectedSubcategory: [] // Changed from selectedSubcategories
            }
        },
        validationSchema: validationSchema,
        onSubmit: (values) => {
            setButtonLoading(true);
            console.log('Form submitted with enhanced selection:', values);

            // Transform the data for API submission - ensure field names match API
            const submissionData = {
                ...(id && { id }),
                name: values.name,
                code: values.code,
                description: values.description,
                expiryDate: values.expiryDate,
                minimumCartAmount: values.minimumCartAmount,
                discountPercentage: values.discountPercentage,
                redeemCount: values.redeemCount,
                image: values.image,

                // Transform back to API expected format
                vendorSelection: {
                    mode: values.vendorSelection.mode,
                    selectedVendors: values.vendorSelection.selectedVendor // Transform back to plural
                },
                brandSelection: {
                    mode: values.brandSelection.mode,
                    selectedBrands: values.brandSelection.selectedBrand // Transform back to plural
                },
                categorySelection: {
                    mode: values.categorySelection.mode,
                    selectedCategories: values.categorySelection.selectedCategory // Transform back to plural
                },
                subcategorySelection: {
                    mode: values.subcategorySelection.mode,
                    selectedSubcategories: values.subcategorySelection.selectedSubcategory // Transform back to plural
                }
            };

            console.log('📤 Transformed submission data:', submissionData);

            // Call the API function
            createUpdateOfferPromocodes(submissionData)((response) => {
                setButtonLoading(false);

                if (response?.status === 200 || response?.status === 201) {
                    toast.success(response?.data?.message || 'Promo code created successfully!');
                    navigate(-1);
                } else {
                    const errorMessage = response?.data?.message || 'Failed to create promo code';
                    toast.error(errorMessage);
                    console.error('API Error:', response);
                }
            });
        },
    });
    // Load manufacturers/vendors
    const loadOptionsManufacture = useCallback((inputValue = '') => {
        return new Promise((resolve) => {
            getManufacturesListingPaginate({
                page: 1,
                limit: 50,
                search: inputValue
            })(response => {
                if (response?.statusCode === 200) {
                    const results = response?.results?.map(item => ({
                        value: item.value || item.id,
                        label: item.label || item.storeName || item.name,
                    }));
                    resolve(results);
                } else {
                    resolve([]);
                }
            });
        });
    }, []);

    // Load brands
    const loadOptionsBrands = useCallback((inputValue = '') => {
        return new Promise((resolve) => {
            getBrandListing({
                page: 1,
                pageSize: 50,
                search: inputValue
            })(response => {
                if (response?.statusCode === 200) {
                    let brandData = response?.data || response?.results || [];
                    const results = brandData.map(item => ({
                        value: item.id,
                        label: item.name
                    }));
                    resolve(results);
                } else {
                    resolve([]);
                }
            });
        });
    }, []);

    // Load categories
    const loadOptionsCategory = useCallback((inputValue = '') => {
        return new Promise((resolve) => {
            getCategoryListingPaginate({
                page: 1,
                limit: 50,
                search: inputValue
            })(response => {
                if (response?.statusCode === 200) {
                    const results = response?.results?.map(item => ({
                        value: item.value || item.id,
                        label: item.label || item.title || item.name,
                    }));
                    resolve(results);
                } else {
                    resolve([]);
                }
            });
        });
    }, []);

    // Load subcategories
    const loadOptionsSubCategory = useCallback((inputValue = '') => {
        return new Promise((resolve) => {
            const categorySelection = formik.values.categorySelection;
            let categoryIds = [];

            console.log(`📋 Category Selection Mode: ${categorySelection.mode}`, {
                selectedCategories: categorySelection.selectedCategory?.length || 0,
                totalCategories: category.length
            });

            // Handle different category selection modes
            if (categorySelection.mode === 'includeAll') {
                // Include all categories - get all category IDs
                categoryIds = category.map(cat => cat.value);
                console.log(`📋 IncludeAll mode - loading subcategories for ALL ${categoryIds.length} categories`);
            }
            else if (categorySelection.mode === 'includeSpecific' && categorySelection.selectedCategory.length > 0) {
                // Include specific categories - only get selected category IDs
                categoryIds = categorySelection.selectedCategory.map(cat => cat.value);
                console.log(`📋 IncludeSpecific mode - loading subcategories for ${categoryIds.length} selected categories`);
            }
            else if (categorySelection.mode === 'excludeAll') {
                // Exclude all categories - NO categories should be included, so no subcategories needed
                console.log(`📋 ExcludeAll mode - NO categories included, returning empty subcategories`);
                resolve([]);
                return;
            }
            else if (categorySelection.mode === 'excludeSpecific' && categorySelection.selectedCategory.length > 0) {
                // Exclude specific categories - get all category IDs EXCEPT the excluded ones
                const excludedCategoryIds = categorySelection.selectedCategory.map(cat => cat.value);
                categoryIds = category
                    .filter(cat => !excludedCategoryIds.includes(cat.value))
                    .map(cat => cat.value);
                console.log(`📋 ExcludeSpecific mode - loading subcategories for ${categoryIds.length} categories (excluding ${excludedCategoryIds.length} categories)`);
            }
            else {
                // Fallback - no valid selection, return empty
                console.log('📋 No valid category selection - returning empty subcategories');
                resolve([]);
                return;
            }

            console.log(`📋 Final category IDs for subcategory fetch:`, categoryIds);

            if (categoryIds.length === 0) {
                console.log('🚫 No categories available for subcategory fetch');
                resolve([]);
                return;
            }

            getSubCategoryListingPaginate({
                page: 1,
                limit: 100,
                search: inputValue,
                categoryIds: categoryIds
            })(response => {
                if (response?.statusCode === 200) {
                    const results = response?.results?.map(item => ({
                        value: item.value || item.id,
                        label: item.categoryName + ' - ' + (item.name),
                    }));
                    console.log(`✅ Loaded ${results.length} subcategories for ${categoryIds.length} categories`);
                    resolve(results);
                } else {
                    console.log('❌ Failed to load subcategories');
                    resolve([]);
                }
            });
        });
    }, [formik.values.categorySelection, category]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const trimmedValue = value.replace(/^\s+/, '');
        formik.setFieldValue(name, trimmedValue);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                formik.setFieldValue("img_view", reader.result);
                formik.setFieldValue("image", reader.result);

            };
            reader.readAsDataURL(file);
        }
    };

    const onDate = (selectedDate) => {
        formik.setFieldValue("expiryDate", selectedDate);
    };

    const handleCancel = () => {
        navigate(-1);
    };

    // Check if subcategory should be disabled
    const isSubCategoryDisabled = () => {
        const categorySelection = formik.values.categorySelection;

        if (category.length === 0) {
            return true;
        }

        // Disable if excluding all categories (no categories left to choose subcategories from)
        if (categorySelection.mode === 'excludeAll') {
            return true;
        }

        // Disable if including specific categories but none are selected
        if (categorySelection.mode === 'includeSpecific' && categorySelection.selectedCategory.length === 0) {
            return true;
        }

        // Disable if excluding specific categories and all categories are excluded
        if (categorySelection.mode === 'excludeSpecific') {
            const excludedCategoryIds = categorySelection.selectedCategory.map(cat => cat.value);
            const availableCategories = category.filter(cat => !excludedCategoryIds.includes(cat.value));
            return availableCategories.length === 0;
        }

        return false;
    };

    // Load initial data
    useEffect(() => {
        // Load manufacturers
        loadOptionsManufacture('').then(manufacturers => {
            setManufacture(manufacturers);
        });

        // Load categories
        loadOptionsCategory('').then(categories => {
            console.log('✅ Categories loaded:', categories.length);
            setCategory(categories);
        });

        // Load brands
        loadOptionsBrands('').then(brands => {
            setBrands(brands);
        });
    }, [loadOptionsManufacture, loadOptionsCategory, loadOptionsBrands]);

    useEffect(() => {
        if (id) {
            setLoading(true);
            console.log('🔄 Fetching promocode data for ID:', id);

            getOfferPromocodeById({ id })((response) => {
                setLoading(false);

                if (response?.status === 200 && response?.data?.data) {
                    const promocodeData = response.data.data;
                    console.log('📦 Raw API data:', promocodeData);

                    // Transform the API response to match our form structure
                    const transformedData = {
                        name: promocodeData.name || "",
                        code: promocodeData.code || "",
                        description: promocodeData.description || "",
                        minimumCartAmount: promocodeData.minimumCartAmount || "",
                        discountPercentage: promocodeData.discountPercentage || "",
                        redeemCount: promocodeData.redeemCount || "",
                        image: promocodeData.image || "",
                        expiryDate: promocodeData.expiryDate ? dayjs(promocodeData.expiryDate).format('YYYY-MM-DD') : "",
                        img_view: promocodeData.image || "",

                        // Transform selection data - CORRECT THE FIELD NAMES
                        vendorSelection: {
                            mode: promocodeData.vendorSelection?.mode || 'includeAll',
                            selectedVendor: promocodeData.vendorSelection?.selectedVendors || [] // Transform to singular
                        },
                        brandSelection: {
                            mode: promocodeData.brandSelection?.mode || 'includeAll',
                            selectedBrand: promocodeData.brandSelection?.selectedBrands || [] // Transform to singular
                        },
                        categorySelection: {
                            mode: promocodeData.categorySelection?.mode || 'includeAll',
                            selectedCategory: promocodeData.categorySelection?.selectedCategories || [] // Transform to singular
                        },
                        subcategorySelection: {
                            mode: promocodeData.subcategorySelection?.mode || 'includeAll',
                            selectedSubcategory: promocodeData.subcategorySelection?.selectedSubcategories || [] // Transform to singular
                        }
                    };

                    console.log('🔄 Transformed data:', transformedData);
                    formik.setValues(transformedData);

                } else {
                    const errorMessage = response?.data?.message || 'Failed to fetch promocode data';
                    toast.error(errorMessage);
                    console.error('❌ Error fetching promocode:', response);
                }
            });
        }
    }, [id]);

    // Load subcategories when category selection changes
    useEffect(() => {
        console.log('🔄 Category selection or categories changed:', {
            mode: formik.values.categorySelection.mode,
            selectedCount: formik.values.categorySelection.selectedCategory?.length || 0,
            totalCategories: category.length,
            existingSubcategories: formik.values.subcategorySelection.selectedSubcategory?.length || 0
        });

        if (!isSubCategoryDisabled() && category.length > 0) {
            console.log('🔄 Refreshing subcategories...');

            // Clear subcategory selections when the available categories change significantly
            const shouldClearSelections =
                (formik.values.categorySelection.mode === 'includeSpecific' &&
                    formik.values.categorySelection.selectedCategory?.length === 0) ||
                (formik.values.categorySelection.mode === 'excludeAll') ||
                (formik.values.categorySelection.mode === 'excludeSpecific' &&
                    isSubCategoryDisabled()); // When all categories are excluded

            if (shouldClearSelections) {
                console.log('🧹 Clearing subcategory selections due to category changes');
                formik.setFieldValue('subcategorySelection.selectedSubcategory', []);
            }

            loadOptionsSubCategory('').then(subCategories => {
                setSubCategory(subCategories);
                console.log('✅ Subcategories loaded:', subCategories.length);

                const currentSubcategories = formik.values.subcategorySelection.selectedSubcategory || [];
                if (currentSubcategories.length > 0) {
                    console.log('💾 Preserving existing subcategory selections:', currentSubcategories.length);

                    // Filter out any subcategories that are no longer valid
                    const validSubcategories = currentSubcategories.filter(subCat =>
                        subCategories.some(newSubCat => newSubCat.value === subCat.value)
                    );

                    if (validSubcategories.length !== currentSubcategories.length) {
                        console.log('🔄 Removing invalid subcategory selections');
                        formik.setFieldValue('subcategorySelection.selectedSubcategory', validSubcategories);
                    }
                }
            });
        } else {
            console.log('🚫 Subcategories disabled - clearing list. Reason:', {
                hasCategories: category.length > 0,
                isDisabled: isSubCategoryDisabled(),
                mode: formik.values.categorySelection.mode
            });

            // Clear subcategories when disabled
            setSubCategory([]);
            if (!id || formik.values.categorySelection.mode === 'excludeAll') {
                formik.setFieldValue('subcategorySelection.selectedSubcategory', []);
            }
        }
    }, [formik.values.categorySelection.mode, formik.values.categorySelection.selectedCategory, category]);
    return (

        <>

            {loading ?

                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <Loader />
                </div>

                :

                <Container fluid className="py-4">
                    <Row className="mb-4">
                        <Col>
                            <div className="page-title-box d-flex align-items-center mb-0">
                                <div
                                    onClick={() => navigate(-1)}
                                    style={{ cursor: 'pointer', fontSize: '24px', fontWeight: '800', display: 'flex', alignItems: 'center' }}
                                >
                                    <IoMdArrowBack className='icon' />
                                </div>
                                <h4 className="mb-0 fw-bold">{id ? 'Edit' : 'Create'} Promocode</h4>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form onSubmit={formik.handleSubmit}>
                                <Row>
                                    {/* Left Side - Offer Details (7 grid) */}
                                    <Col md={12}>
                                        <Card className="mb-4 shadow-sm">
                                            <Card.Header className="bg-light">
                                                <span style={{ fontSize: '15px', fontWeight: '600' }}>
                                                    Offer Details
                                                </span>
                                            </Card.Header>
                                            <Card.Body className="py-3 px-4">
                                                <Row>
                                                    <Row className="md-12">
                                                        <Col md={8}>
                                                            <Row>
                                                                <Col md={6}>
                                                                    <Form.Group className="mb-2">
                                                                        <Form.Label className="fw-semibold" style={{ fontSize: "13px" }}>
                                                                            Enter offer name *
                                                                        </Form.Label>
                                                                        <Form.Control
                                                                            type="text"
                                                                            placeholder="Enter offer name"
                                                                            name="name"
                                                                            value={formik.values?.name}
                                                                            onChange={handleInputChange}
                                                                            onBlur={formik.handleBlur}
                                                                            isInvalid={formik.touched.name && !!formik.errors.name}
                                                                            className="rounded-input"
                                                                            style={{
                                                                                borderRadius: "8px",
                                                                                padding: "8px 10px",
                                                                                fontSize: "13px",
                                                                            }}
                                                                        />
                                                                        <Form.Control.Feedback type="invalid">
                                                                            {formik.errors.name}
                                                                        </Form.Control.Feedback>
                                                                    </Form.Group>
                                                                </Col>

                                                                <Col md={6}>
                                                                    <Form.Group className="mb-2">
                                                                        <Form.Label className="fw-semibold" style={{ fontSize: "13px" }}>
                                                                            Code *
                                                                        </Form.Label>
                                                                        <Form.Control
                                                                            type="text"
                                                                            placeholder="Enter offer code"
                                                                            name="code"
                                                                            value={formik.values?.code}
                                                                            onChange={handleInputChange}
                                                                            onBlur={formik.handleBlur}
                                                                            isInvalid={formik.touched.code && !!formik.errors.code}
                                                                            className="rounded-input"
                                                                            style={{
                                                                                borderRadius: "8px",
                                                                                padding: "8px 10px",
                                                                                fontSize: "13px",
                                                                            }}
                                                                        />
                                                                        <Form.Control.Feedback type="invalid">
                                                                            {formik.errors.code}
                                                                        </Form.Control.Feedback>
                                                                    </Form.Group>
                                                                </Col>
                                                            </Row>


                                                            <Row>
                                                                <Col md={6}>
                                                                    <Form.Group className="mb-2">
                                                                        <Form.Label className="fw-semibold" style={{ fontSize: "13px" }}>
                                                                            Minimum Cart Amount *
                                                                        </Form.Label>
                                                                        <Form.Control
                                                                            type="number"
                                                                            placeholder="Enter minimum cart amount"
                                                                            name="minimumCartAmount"
                                                                            value={formik.values?.minimumCartAmount}
                                                                            onChange={handleInputChange}
                                                                            onBlur={formik.handleBlur}
                                                                            isInvalid={formik.touched.minimumCartAmount && !!formik.errors.minimumCartAmount}
                                                                            className="rounded-input"
                                                                            style={{
                                                                                borderRadius: "8px",
                                                                                padding: "8px 10px",
                                                                                fontSize: "13px",
                                                                            }}
                                                                            min="0"
                                                                            step="0.01"
                                                                        />
                                                                        <Form.Control.Feedback type="invalid">
                                                                            {formik.errors.minimumCartAmount}
                                                                        </Form.Control.Feedback>
                                                                    </Form.Group>
                                                                </Col>

                                                                <Col md={6}>
                                                                    <Form.Group className="mb-2">
                                                                        <Form.Label className="fw-semibold" style={{ fontSize: "13px" }}>
                                                                            Discount Percentage % *
                                                                        </Form.Label>
                                                                        <Form.Control
                                                                            type="text"
                                                                            placeholder="Enter discount percentage"
                                                                            name="discountPercentage"
                                                                            value={formik.values?.discountPercentage}
                                                                            onChange={handleInputChange}
                                                                            onBlur={formik.handleBlur}
                                                                            isInvalid={formik.touched.discountPercentage && !!formik.errors.discountPercentage}
                                                                            className="rounded-input"
                                                                            style={{
                                                                                borderRadius: "8px",
                                                                                padding: "8px 10px",
                                                                                fontSize: "13px",
                                                                            }}
                                                                            min="1"
                                                                            max="100"
                                                                            step="0.01"
                                                                        />
                                                                        <Form.Control.Feedback type="invalid">
                                                                            {formik.errors.discountPercentage}
                                                                        </Form.Control.Feedback>
                                                                    </Form.Group>
                                                                </Col>
                                                            </Row>

                                                            <Row>

                                                                <Col md={6}>
                                                                    <Form.Group className="mb-2">
                                                                        <Form.Label className="fw-semibold mb-0" style={{ fontSize: "13px" }}>
                                                                            Expiration Date *
                                                                        </Form.Label>
                                                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                                            <DemoContainer components={["DatePicker"]}>
                                                                                <DatePicker
                                                                                    name="expiryDate"
                                                                                    value={dayjs(formik.values.expiryDate)}
                                                                                    onChange={(newValue) => {
                                                                                        const formattedDate = dayjs(newValue).format("YYYY-MM-DD");
                                                                                        onDate(formattedDate);
                                                                                    }}
                                                                                    format="DD/MM/YYYY"
                                                                                    label=""
                                                                                    disablePast
                                                                                    slotProps={{
                                                                                        textField: {
                                                                                            size: 'small',
                                                                                            fullWidth: true,
                                                                                            error: formik.touched.expiryDate && Boolean(formik.errors.expiryDate),
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            </DemoContainer>
                                                                        </LocalizationProvider>
                                                                        {formik.touched.expiryDate && formik.errors.expiryDate && (
                                                                            <div className="text-danger small mt-1">{formik.errors.expiryDate}</div>
                                                                        )}
                                                                    </Form.Group>
                                                                </Col>

                                                                <Col md={6}>
                                                                    <Form.Group className="mb-2">
                                                                        <Form.Label className="fw-semibold" style={{ fontSize: "13px" }}>
                                                                            Redeem Count *
                                                                        </Form.Label>
                                                                        <Form.Control
                                                                            type="text"
                                                                            placeholder="Enter redeem count"
                                                                            name="redeemCount"
                                                                            value={formik.values?.redeemCount}
                                                                            onChange={handleInputChange}
                                                                            onBlur={formik.handleBlur}
                                                                            isInvalid={formik.touched.redeemCount && !!formik.errors.redeemCount}
                                                                            className="rounded-input"
                                                                            style={{
                                                                                borderRadius: "8px",
                                                                                padding: "8px 10px",
                                                                                fontSize: "13px",
                                                                            }}
                                                                            min="1"
                                                                            step="1"
                                                                        />
                                                                        <Form.Control.Feedback type="invalid">
                                                                            {formik.errors.redeemCount}
                                                                        </Form.Control.Feedback>

                                                                    </Form.Group>
                                                                </Col>



                                                            </Row>


                                                        </Col>

                                                        <Col md={4}>
                                                            <Form.Group controlId="promoImage" className="mb-0">
                                                                <Form.Control
                                                                    type="file"
                                                                    accept="image/*"
                                                                    onChange={handleImageChange}
                                                                    ref={fileInputRef}
                                                                    style={{ display: "none" }}
                                                                />

                                                                <div
                                                                    onClick={() => fileInputRef.current.click()}
                                                                    className="position-relative border border-2 border-dashed rounded d-flex align-items-center justify-content-center overflow-hidden"
                                                                    style={{
                                                                        height: "240px",
                                                                        cursor: "pointer",
                                                                    }}
                                                                >
                                                                    {formik.values.img_view ? (
                                                                        <>
                                                                            <img
                                                                                src={formik.values.img_view}
                                                                                alt="promocode"
                                                                                className="w-100 h-100"
                                                                                style={{
                                                                                    objectFit: "contain",
                                                                                    transition: "0.3s ease",
                                                                                }}
                                                                            />
                                                                            <div
                                                                                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center text-white fw-semibold"
                                                                                style={{
                                                                                    background: "rgba(0, 0, 0, 0)",
                                                                                    opacity: 0,
                                                                                    transition: "all 0.3s ease",
                                                                                }}
                                                                                onMouseEnter={(e) =>
                                                                                    (e.currentTarget.style.background = "rgba(0,0,0,0.6)")
                                                                                }
                                                                                onMouseLeave={(e) =>
                                                                                    (e.currentTarget.style.background = "rgba(0,0,0,0)")
                                                                                }
                                                                            >
                                                                                Update Image
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        <div className="text-center text-muted">
                                                                            <FiImage size={42} className="mb-2" />
                                                                            <div style={{ fontSize: "13px" }}>Click to upload image</div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>

                                                    <Col md={12}>
                                                        <Form.Group className="mb-2">
                                                            <Form.Label className="fw-semibold" style={{ fontSize: "13px" }}>
                                                                Offer description *
                                                            </Form.Label>
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={4}
                                                                placeholder="Enter offer description"
                                                                name="description"
                                                                value={formik.values?.description}
                                                                onChange={handleInputChange}
                                                                onBlur={formik.handleBlur}
                                                                isInvalid={formik.touched.description && !!formik.errors.description}
                                                                className="rounded-input"
                                                                style={{
                                                                    borderRadius: "8px",
                                                                    padding: "8px 10px",
                                                                    fontSize: "13px",
                                                                }}
                                                            />
                                                            <Form.Control.Feedback type="invalid">
                                                                {formik.errors.description}
                                                            </Form.Control.Feedback>
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Separate Cards for Each Parameter */}
                                {user?.userType !== "Vendor" && (
                                    <ParameterSelectionCard
                                        title="Vendors"
                                        selectionType="vendor"
                                        loadOptions={loadOptionsManufacture}
                                        defaultOptions={manufacture}
                                        formik={formik}
                                    />
                                )}

                                <ParameterSelectionCard
                                    title="Brands"
                                    selectionType="brand"
                                    loadOptions={loadOptionsBrands}
                                    defaultOptions={brands}
                                    formik={formik}
                                />

                                <ParameterSelectionCard
                                    title="Categories"
                                    selectionType="category"
                                    loadOptions={loadOptionsCategory}
                                    defaultOptions={category}
                                    hideExcludeAll={true}
                                    formik={formik}
                                />

                                <ParameterSelectionCard
                                    title="Subcategories"
                                    selectionType="subcategory"
                                    loadOptions={loadOptionsSubCategory}
                                    defaultOptions={subcategory}
                                    formik={formik}
                                    disabled={isSubCategoryDisabled()}
                                    hideExcludeAll={true}
                                    isSubCategory={true}
                                />

                                <PromoCodeSummary formik={formik} />

                                {/* Action Buttons */}
                                <div className="mt-4 d-flex gap-2 justify-content-end">
                                    <Button
                                        variant="secondary"
                                        onClick={handleCancel}
                                        className="cancel_btn"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={buttonLoading}
                                        className="request_btn"
                                        style={{
                                            minWidth: '150px', // Set to accommodate the longest text "Updating..."
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        {buttonLoading ? (
                                            <>
                                                <Spinner animation="border" size="sm" />
                                                {id ? "Updating Promo..." : "Creating Promo..."}
                                            </>
                                        ) : (
                                            id ? "Update Promo" : "Create Promo"
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Col>
                    </Row>
                </Container>}

        </>

    );
};

export default CreatePromoCodesPage;